import type { BadgeTone } from '../design-system/atoms/Badge/Badge';

/** 실행 슬롯 상태. S 성공 · Y 재시도 성공 · F 실패 · R 진행 · U 예정 · _ 누락/미실행. */
export type RunState = 'S' | 'Y' | 'F' | 'R' | 'U' | '_';

/** RETRY_SUCCESS: 실패했던 Job을 재시도해 성공한 상태(정방향 재시도 Job 존재). */
export type JobStatus = 'QUEUED' | 'RUNNING' | 'SUCCESS' | 'FAILED' | 'RETRY_SUCCESS';

/** Job 상태 → 배지 색 계열. 시안 JOB_STATUS 클래스 매핑에서 추출. */
export function jobStatusTone(status: JobStatus): BadgeTone {
  switch (status) {
    case 'RUNNING':
      return 'info';
    case 'SUCCESS':
    case 'RETRY_SUCCESS':
      return 'success';
    case 'FAILED':
      return 'danger';
    default:
      return 'neutral';
  }
}

/** 대시보드 모듈 헬스 행. module_config + ingest_job 집계 결과. */
export interface ModuleHealth {
  name: string;
  /** 최근 실행 상태 문자열. 예: "SSSFSR__" */
  runs: string;
  cron: string;
  last: string;
  /** 최근 성공률 0~100. */
  rate: number;
  avg: string;
  next: string;
  /** 확인 필요 상태 여부. */
  bad: boolean;
}

export interface ScheduleSlot {
  /** 0~24 시각. started 기반일 때는 분 단위 소수 포함. */
  hour: number;
  state: Exclude<RunState, '_'>;
  /** 실행 소요 시간(시간 단위). 있으면 막대 width로, 없으면 점으로 표시. */
  durationHours?: number;
  /** hover 툴팁 내용. 과거 실행 상세(조회 결과)에만 채워지고, 예정 슬롯은 비움. */
  tooltip?: string;
  /** 연결된 Job ID. 과거 실행 막대에만 있고(클릭 시 상세 이동), 예정 슬롯은 없음. */
  jobId?: number;
  /** 수동 실행(trigger==='manual') 여부. 막대에 테두리를 줘 자동(cron) 실행과 구분. */
  manual?: boolean;
}

/** 오늘 편성표 한 행. cron 전개 + ingest_job 매칭 결과. */
export interface ScheduleRow {
  name: string;
  slots: ScheduleSlot[];
  /** 이 행 위에 그룹 구분 여백을 둘지. 실행 이력 없는(히트맵 밖) 모듈 그룹의 첫 행에 표시. */
  groupBreak?: boolean;
}

/** module_config 한 건. */
export interface ModuleConfig {
  name: string;
  description: string;
  enabled: boolean;
  /** 수집기 미연결 스텁 여부. */
  stub: boolean;
  cronExpr?: string;
  collectorType?: string;
  collectorOptions?: string[];
  nextRunAt?: string;
  inputJson?: string;
}

/** 수집 이력 목록 행. */
export interface JobRow {
  id: number;
  module: string;
  trigger: string;
  collector: string;
  status: JobStatus;
  posts: number | null;
  comments: number | null;
  created: string;
  durationLabel: string;
}

/** Job 상세. */
export interface JobDetailData {
  id: number;
  module: string;
  trigger: string;
  collector: string;
  status: JobStatus;
  posts: number | null;
  comments: number | null;
  collectionId: string | null;
  created: string;
  started: string | null;
  finished: string | null;
  inputJson: string;
  errorText: string | null;
  /** 재시도로 생성된 Job이면 원본 Job ID. 아니면 null. */
  retryOfJobId: number | null;
  /** 최신 재시도 Job ID(재시도 결과). RETRY_SUCCESS일 때 목록 역참조로 채워짐. 아니면 null. */
  latestRetryId: number | null;
}

export interface TimelineStep {
  label: string;
  time: string | null;
  done: boolean;
}
